import { lazy, Suspense, useEffect, useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getGameById } from "../data/games";
import usePageMeta from "../hooks/usePageMeta";
import { trackEvent } from "../utils/analytics";
import GameCountdown from "../components/GameCountdown";

const COMPONENTS = {
  spin: lazy(() => import("../components/games/SpinWheel")),
  cups: lazy(() => import("../components/games/CupAndBalls")),
  memory: lazy(() => import("../components/games/MemoryMatch")),
  balloon: lazy(() => import("../components/games/BalloonPop")),
  scratch: lazy(() => import("../components/games/LuckyScratch")),
  tap: lazy(() => import("../components/games/QuickTap")),
  inflate: lazy(() => import("../components/games/BalloonInflator")),
  whack: lazy(() => import("../components/games/WhackAMole")),
  stack: lazy(() => import("../components/games/StackTower")),
  cookie: lazy(() => import("../components/games/CookieCruncher")),
  reflex: lazy(() => import("../components/games/ReflexDuel")),
  tug: lazy(() => import("../components/games/BumperTug")),
  popit: lazy(() => import("../components/games/PopItRush")),
  slime: lazy(() => import("../components/games/SlimeSlap")),
  sumo: lazy(() => import("../components/games/FingerSumo")),
  sniper: lazy(() => import("../components/games/TargetSniper")),
  pendulum: lazy(() => import("../components/games/PendulumDrop")),
  orbit: lazy(() => import("../components/games/OrbitLauncher")),
  golf: lazy(() => import("../components/games/GolfHole")),
  hockey: lazy(() => import("../components/games/AirHockey")),
  sub: lazy(() => import("../components/games/SubmarineTorpedo")),
  pinball: lazy(() => import("../components/games/MagnetCatch")),
  magnet: lazy(() => import("../components/games/MagnetCatch")),
  asteroid: lazy(() => import("../components/games/AsteroidBumper")),
  volley: lazy(() => import("../components/games/VolleyballBounce")),
  slicer: lazy(() => import("../components/games/FruitSlicer")),
  cst: lazy(() => import("../components/games/ColorSwitchTap")),
  mathquiz: lazy(() => import("../components/games/MathQuiz")),
  count: lazy(() => import("../components/games/NumberCount")),
  multiply: lazy(() => import("../components/games/MultiplyMaster")),
  wordprob: lazy(() => import("../components/games/WordProblem")),
  trivia: lazy(() => import("../components/games/TriviaMCQ")),
  ttt: lazy(() => import("../components/games/TicTacToe")),
  snakes: lazy(() => import("../components/games/SnakesLadders")),
  scramble: lazy(() => import("../components/games/WordScramble")),
  odd: lazy(() => import("../components/games/OddOneOut")),
  simon: lazy(() => import("../components/games/SimonMemory")),
  merge: lazy(() => import("../components/games/MergeBlast")),
  linerace: lazy(() => import("../components/games/LineRacer")),
  maze: lazy(() => import("../components/games/MazeRush")),
};

const GAME_DESCRIPTIONS = {
  spin: "Free spin every time. Land on a coin number to win those coins!",
  cups: "Watch which cup hides the ball, then tap the right one to win 30 coins.",
  memory: "Flip the cards and match the pairs. The fewer tries, the more coins.",
  balloon: "Pop as many balloons as you can before time runs out — 5 coins each.",
  scratch: "Drag your finger across the card to reveal your surprise prize.",
  tap: "Tap the target as many times as you can in 10 seconds. Speed = coins.",
  inflate: "Pump the balloon big, but stop before it bursts! Bigger = more coins.",
  whack: "Moles pop out of holes — bonk them fast to earn 5 coins each.",
  stack: "Tap to drop each floor. Miss and it's game over. Build the tallest tower!",
  cookie: "Rapid-tap the giant cookie to bite off as many pieces as you can.",
  reflex: "Wait for the green GO! then tap as fast as you can. Faster = more coins.",
  tug: "Tap PULL as fast as you can to drag the marker to your flag before the AI does.",
  popit: "Pop every bubble on the board. Finish under 12s for a big speed bonus!",
  slime: "Slap the wobbling slimes before they slip away. 6 coins per slap in 15s.",
  sumo: "Rapid-tap SHOVE to push the robot off the sumo ring — 100 coins if you win.",
  sniper: "Tap moving targets that shrink each round. Bullseye earns 30 coins per hit.",
  pendulum: "Time your DROP as the pendulum swings — land on the target for 40 coins!",
  orbit: "Wait for your ball to line up with the target, then LAUNCH into space.",
  golf: "First tap locks angle, second tap locks power. Sink it for 200 coins!",
  hockey: "Move your paddle to defend and score. First to 3 goals wins 125 coins.",
  sub: "Steer your sub, fire torpedoes, and blast passing ships — 12 coins each.",
  pinball: "Slide the magnet under falling metal — dodge the junk. 10 coins per catch.",
  magnet: "Slide the magnet under falling metal to catch it — dodge junk. 10 coins per catch.",
  asteroid: "Swerve past road signs and water puddles for 15 seconds. Every second = 10 coins.",
  volley: "Slide your paddle to keep the ball off your floor. First to 5 points wins 120 coins.",
  slicer: "Swipe your finger over falling fruit to slice them for 5 coins each — but avoid the bombs!",
  cst: "Tap only when the color word MATCHES the button color. Miss the matches or tap on mismatches to lose points.",
  mathquiz: "Pick your age level and solve 6 questions. Every right answer earns 15 coins!",
  count: "Count only the target critter on screen and tap the right number. Bigger rounds, bigger coins.",
  multiply: "Pick a times table and answer as many as you can in 30 seconds — 8 coins per correct.",
  wordprob: "Read a short story, work out the math, and pick the right answer. 30 coins per correct.",
  trivia: "Six multiple-choice trivia questions across animals, science and more. 18 coins per correct.",
  ttt: "Three in a row wins. Beat the AI (+120) or a friend in 2-player mode (+60 each, +20 for a draw).",
  snakes: "Roll the dice, chase ladders, dodge snakes. Reach square 25 before the AI (+150) or a friend in 2-player mode (+75).",
  scramble: "Tap letters in the right order to unscramble the word. 5 rounds — 25 coins per correct answer.",
  odd: "Six pictures, but one is different. Tap the odd one out. 6 rounds — 20 coins per correct spot.",
  simon: "Watch the color pattern, then tap it back. Every round adds one more color. 15 coins per level cleared.",
  merge: "Slide the neon tiles — matching numbers double up! Beat 30 levels, ride the 60s Rush, or grab the Daily bonus. Combos multiply your score.",
  linerace: "Tap ◀ ▶ to switch lanes and dodge oncoming traffic for 20 seconds. Every second = 10 coins.",
  maze: "Steer the mouse 🐭 through a fresh maze to the cheese 🧀 before the 60-second clock runs out. Faster = more coins.",
};

const GameLoading = () => (
  <div className="flex items-center justify-center py-10" data-testid="game-loading">
    <div className="text-4xl bob" aria-hidden>🎮</div>
  </div>
);

const GamePlay = () => {
  const { id } = useParams();
  const meta = getGameById(id);
  usePageMeta(
    meta ? `${meta.name} · 67Cafe Games` : "Game · 67Cafe",
    meta
      ? `Play ${meta.name} on 67Cafe — ${meta.tagline}. Earn coins to spend in the kids' pretend store.`
      : "Play a game on 67Cafe"
  );
  const trackedRef = useRef("");
  const hostRef = useRef(null);
  useEffect(() => {
    if (meta && trackedRef.current !== id) {
      trackedRef.current = id;
      trackEvent("game_play", { game_id: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!meta) return <Navigate to="/games" replace />;
  const Game = COMPONENTS[id];
  if (!Game) return <Navigate to="/games" replace />;

  return (
    <div
      className="mx-auto max-w-3xl px-4 sm:px-6 py-6 sm:py-8"
      data-testid={`gameplay-page-${id}`}
    >
      <Link
        to="/games"
        className="btn-3d btn-sm btn-ghost mb-4"
        data-testid="gameplay-back-btn"
      >
        <ArrowLeft size={18} strokeWidth={3} /> All games
      </Link>

      <div
        className="chunky-card p-4 sm:p-6 mb-6 flex items-center gap-4"
        style={{ background: meta.color + "22" }}
      >
        <div
          className="rounded-full flex items-center justify-center shrink-0"
          style={{
            width: 68,
            height: 68,
            background: meta.color,
            border: "3px solid #2b2b2b",
            boxShadow: "0 4px 0 #2b2b2b",
          }}
        >
          <span className="text-3xl bob" aria-hidden="true">
            {meta.emoji}
          </span>
        </div>
        <div>
          <h1
            className="font-display text-2xl sm:text-3xl leading-tight"
            data-testid="gameplay-title"
          >
            {meta.name}
          </h1>
          <p className="text-sm sm:text-base" style={{ color: "#484848" }}>
            {GAME_DESCRIPTIONS[id]}
          </p>
        </div>
      </div>

      <div className="chunky-card p-4 sm:p-6 relative">
        <Suspense fallback={<GameLoading />}>
          <div ref={hostRef}>
            <Game />
          </div>
        </Suspense>
        <GameCountdown hostRef={hostRef} gameId={id} />
      </div>
    </div>
  );
};

export default GamePlay;
